import { useMemo } from "react"
import { useTranslation } from "react-i18next"
import { PlatformSubSection } from "@tracabois/template"
import { Box, Stack } from "@mui/material"
import { InputForm } from "@komune-io/g2-forms"
import { RealizationUpdateCommand } from "../../../model/realizationModel"
import { SelectFormField } from "../../SelectFormField"
import { useBuildingTypeAllQuery } from "../../../../../referentiels/buildingType/api/BuildingTypeApi"
import { useOwnershipTypeAllQuery } from "../../../../../referentiels/ownershipType/api/OwnershipTypeApi"

interface RealizationInfoSectionProps {
  realization?: Realization
  formValues: Partial<RealizationUpdateCommand>
  onChange: (values: Partial<RealizationUpdateCommand>) => void
  readOnly?: boolean
}

const useLabels = () => {
  const { t } = useTranslation()
  return useMemo(() => ({
    title: t("realization.info.title", "Informations generales"),
    name: t("realization.name", "Nom du projet"),
    deliveryYear: t("realization.deliveryYear", "Année de réalisation"),
    codeInsee: t("realization.codeInsee", "Code INSEE"),
    commune: t("realization.commune", "Commune"),
    codePostal: t("realization.codePostal", "Code postal"),
    qrcode: t("realization.qrcode", "Identifiant QR code"),
    buildingType: t("realization.buildingType", "Type de bâtiment"),
    ownershipType: t("realization.ownershipType", "Type de propriété"),
    latitude: t("realization.latitude", "Latitude"),
    longitude: t("realization.longitude", "Longitude"),
    artIndex: t("realization.artIndex", "Index Art"),
    externalId: t("realization.externalId", "Identifiant externe"),
  }), [t])
}

export const RealizationInfoSection = (props: RealizationInfoSectionProps) => {
  const { formValues, onChange, readOnly = false } = props
  const labels = useLabels()
  const buildingTypeQuery = useBuildingTypeAllQuery()
  const ownershipTypeQuery = useOwnershipTypeAllQuery()

  const buildingTypeLabels: Record<string, string> = useMemo(() => {
    const result: Record<string, string> = {}
    for (const item of buildingTypeQuery.data?.items ?? []) {
      result[item.id] = item.name
    }
    return result
  }, [buildingTypeQuery.data])

  const ownershipTypeLabels: Record<string, string> = useMemo(() => {
    const result: Record<string, string> = {}
    for (const item of ownershipTypeQuery.data?.items ?? []) {
      result[item.id] = item.name
    }
    return result
  }, [ownershipTypeQuery.data])

  const handleChange = (field: keyof RealizationUpdateCommand) => (value: string | number | undefined) => {
    onChange({ ...formValues, [field]: value })
  }

  const handleCoordinateChange = (field: 'latitude' | 'longitude') => (value: string | number | undefined) => {
    const numValue = typeof value === 'string' ? parseFloat(value) : value
    onChange({
      ...formValues,
      coordinates: {
        latitude: formValues.coordinates?.latitude || 0,
        longitude: formValues.coordinates?.longitude || 0,
        [field]: numValue || 0
      }
    })
  }

  return (
    <PlatformSubSection title={labels.title}>
      <Stack spacing={2}>
        {/* Row 1: Nom du projet | Année de réalisation */}
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
          <Box flex={2}>
            <InputForm
              inputType="textField"
              label={labels.name}
              value={formValues.name || ""}
              onChange={handleChange("name")}
              readOnly={readOnly}
            />
          </Box>
          <Box flex={1}>
            <InputForm
              inputType="textField"
              label={labels.deliveryYear}
              value={formValues.deliveryYear?.toString() || ""}
              onChange={(val) => handleChange("deliveryYear")(val ? parseInt(val as string) : undefined)}
              readOnly={readOnly}
            />
          </Box>
        </Stack>

        {/* Row 2: Commune | Code postal | Code INSEE */}
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
          <Box flex={1}>
            <InputForm
              inputType="textField"
              label={labels.commune}
              value={formValues.commune || ""}
              onChange={handleChange("commune")}
              readOnly={readOnly}
            />
          </Box>
          <Box flex={1}>
            <InputForm
              inputType="textField"
              label={labels.codePostal}
              value={formValues.codePostal || ""}
              onChange={handleChange("codePostal")}
              readOnly={readOnly}
            />
          </Box>
          <Box flex={1}>
            <InputForm
              inputType="textField"
              label={labels.codeInsee}
              value={formValues.codeInsee || ""}
              onChange={handleChange("codeInsee")}
              readOnly={readOnly}
            />
          </Box>
        </Stack>

        {/* Row 3: Latitude | Longitude */}
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
          <Box flex={1}>
            <InputForm
              inputType="textField"
              label={labels.latitude}
              value={formValues.coordinates?.latitude?.toString() || ""}
              onChange={handleCoordinateChange("latitude")}
              readOnly={readOnly}
            />
          </Box>
          <Box flex={1}>
            <InputForm
              inputType="textField"
              label={labels.longitude}
              value={formValues.coordinates?.longitude?.toString() || ""}
              onChange={handleCoordinateChange("longitude")}
              readOnly={readOnly}
            />
          </Box>
        </Stack>

        {/* Row 4: Building Type | Ownership Type */}
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
          <Box flex={1}>
            <SelectFormField
              label={labels.buildingType}
              value={formValues.buildingType}
              labels={buildingTypeLabels}
              onChange={(val) => onChange({ ...formValues, buildingType: val ? { id: val } as any : undefined })}
              readOnly={readOnly}
            />
          </Box>
          <Box flex={1}>
            <SelectFormField
              label={labels.ownershipType}
              value={formValues.ownershipType}
              labels={ownershipTypeLabels}
              onChange={(val) => onChange({ ...formValues, ownershipType: val ? { id: val } as any : undefined })}
              readOnly={readOnly}
            />
          </Box>
        </Stack>

        {/* Row 5: Identifiant QR code | Art Index | Identifiant externe */}
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
          <Box flex={1}>
            <InputForm
              inputType="textField"
              label={labels.qrcode}
              value={formValues.redirectPath || ""}
              onChange={handleChange("redirectPath")}
              readOnly={readOnly}
            />
          </Box>
          <Box flex={1}>
            <InputForm
              inputType="textField"
              label={labels.artIndex}
              value={formValues.artIndex || ""}
              onChange={handleChange("artIndex")}
              readOnly={readOnly}
            />
          </Box>
          <Box flex={1}>
            <InputForm
              inputType="textField"
              label={labels.externalId}
              value={formValues.externalId || ""}
              onChange={handleChange("externalId")}
              readOnly={readOnly}
            />
          </Box>
        </Stack>
      </Stack>
    </PlatformSubSection>
  )
}
